import { LinkBase, useGetList } from 'ra-core'
import { Badge } from '@internal/ui-kit/components/ui/badge'
import { buttonVariants } from '@internal/ui-kit/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@internal/ui-kit/components/ui/card'
import type { subscriberSchema } from '@/apps/subscribers/schema'
import type { z } from 'zod'

type Subscriber = z.infer<typeof subscriberSchema> & { id: string }

/**
 * User detail page widget (this ticket; review feedback round 2: "show
 * subscription as a separate card under user profile. Allow to open
 * subscription detail page from there") — one `Card` per subscriber record,
 * each linking to `apps/subscribers`' own detail page
 * (`/subscribers/:id`). `GET /v2/admin/subscribers?email=` (`apps/subscribers`'
 * `dataProvider` resource) can return more than one row per email (one per
 * list a user separately opted into), so this renders every matching row
 * rather than assuming exactly one.
 *
 * Rendered inside `ProfileTab.tsx`, not its own tab (round 5 review: "move
 * subscription card directly to the same tab with profile itself") — the
 * name stays `SubscriptionsTab` since it's still the same self-contained
 * widget, just embedded rather than routed to.
 */
export function SubscriptionsTab({ email }: { email: string | undefined }) {
  const { data, isPending } = useGetList<Subscriber>(
    'subscribers',
    { pagination: { page: 1, perPage: 100 }, filter: { email } },
    { enabled: Boolean(email) },
  )
  const subscriptions = data ?? []

  if (isPending) return <p className="text-muted-foreground text-sm">Loading subscriptions…</p>
  if (subscriptions.length === 0) return <p className="text-muted-foreground text-sm">No subscription on file.</p>

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {subscriptions.map((subscription) => (
        <Card key={subscription.subscriber_id}>
          <CardHeader>
            <CardTitle className="flex items-center justify-between gap-2">
              <Badge variant="outline">{subscription.status}</Badge>
              <LinkBase
                to={`/subscribers/${subscription.subscriber_id}`}
                className={buttonVariants({ variant: 'outline', size: 'sm' })}
              >
                View details
              </LinkBase>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-muted-foreground text-sm">Lists:</span>
              {subscription.lists.length === 0
                ? '—'
                : subscription.lists.map((list) => (
                    <Badge key={list} variant="secondary">
                      {list}
                    </Badge>
                  ))}
            </div>
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-muted-foreground text-sm">Unsubscribed topics:</span>
              {subscription.unsubscribed_topics.length === 0
                ? '—'
                : subscription.unsubscribed_topics.map((topic) => (
                    <Badge key={topic} variant="secondary">
                      {topic}
                    </Badge>
                  ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
